import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  AUTH_CHANGED_EVENT,
  getSavedJobs,
  isAuthenticated,
  toggleSavedJob
} from '../utils/userData';
import './Jobs.css';

const Jobs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [keyword, setKeyword] = useState(location.state?.keyword || 'java');
  const [searchLocation, setSearchLocation] = useState(location.state?.location || 'india');
  const [savedJobIds, setSavedJobIds] = useState(new Set());
  const initialKeyword = location.state?.keyword || 'java';
  const initialLocation = location.state?.location || 'india';

  useEffect(() => {
    const syncSavedJobs = () => {
      const ids = getSavedJobs().map((savedJob) => String(savedJob.id));
      setSavedJobIds(new Set(ids));
    };

    syncSavedJobs();
    window.addEventListener(AUTH_CHANGED_EVENT, syncSavedJobs);

    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, syncSavedJobs);
    };
  }, []);

  const fetchJobs = useCallback(async (keywordToUse, locationToUse, page = 1) => {
    try {
      setLoading(true);
      // Using your backend endpoint
      const response = await axios.get('http://localhost:8080/fetch-jobs', {
        params: {
          keyword: keywordToUse,
          location: locationToUse,
          page: page
        }
      });
      
      setJobs(response.data.results || []);
      // Assuming backend returns page info, otherwise set a default
      setTotalPages(response.data.total_pages || Math.ceil((response.data.count || 0) / 10));
      setCurrentPage(page);
      
      toast.success(`Found ${response.data.count || 0} opportunities!`);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Unable to fetch opportunities. Please try again.');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs(initialKeyword, initialLocation, 1);
  }, [initialKeyword, initialLocation, fetchJobs]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchJobs(keyword, searchLocation, 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      fetchJobs(keyword, searchLocation, currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      fetchJobs(keyword, searchLocation, currentPage + 1);
    }
  };

  const handleSaveJob = (job) => {
    if (!isAuthenticated()) {
      toast.info('Please login or sign up to save jobs.');
      navigate('/job-login');
      return;
    }

    const added = toggleSavedJob(job);
    setSavedJobIds((prev) => {
      const next = new Set(prev);
      if (added) {
        next.add(String(job.id));
      } else {
        next.delete(String(job.id));
      }
      return next;
    });

    if (added) {
      toast.success('Job saved to your collection.');
    } else {
      toast.info('Job removed from saved collection.');
    }
  };

  const formatSalary = (min, max) => {
    if (!min || !max) return 'Competitive package';
    return `₹${(min / 100000).toFixed(0)}L - ₹${(max / 100000).toFixed(0)}L`;
  };

  const handleViewDetails = (job) => {
    // Navigate to job details page and pass job data
    navigate(`/jobs/${job.id}`, { state: { job } });
  };

  const getCompanyName = (job) => {
    // Backend returns snake_case: display_name
    return job.company?.display_name || 
           job.company?.displayName ||
           job.company?.name || 
           (typeof job.company === 'string' ? job.company : 'Company');
  };

  const getLocation = (job) => {
    // Backend returns snake_case: display_name
    return job.location?.display_name || 
           job.location?.displayName ||
           job.location?.name || 
           (typeof job.location === 'string' ? job.location : 'Location');
  };

  return (
    <div className="jobs-page">
      <Navbar />
      
      <div className="jobs-container">
        <div className="jobs-header">
          <h1>Browse Premium Opportunities</h1>
          <p className="jobs-subtitle">Curated roles from India's most innovative companies</p>
          
          <form className="jobs-search-bar" onSubmit={handleSearch}>
            <input
              type="text"
              placeholder="Role or expertise"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="jobs-search-input"
            />
            <input
              type="text"
              placeholder="Location"
              value={searchLocation}
              onChange={(e) => setSearchLocation(e.target.value)}
              className="jobs-search-input"
            />
            <button type="submit" className="jobs-search-btn">Search</button>
          </form>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="loader"></div>
            <p>Discovering premium opportunities...</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="empty-state">
            <h3>No opportunities found</h3>
            <p>Try adjusting your search criteria</p>
          </div>
        ) : (
          <>
            <div className="jobs-grid">
              {jobs.map((job) => (
                <div key={job.id} className="job-card">
                  <h3 className="job-title">{job.title}</h3>
                  <div className="job-company">⚡ {getCompanyName(job)}</div>
                  <div className="job-location">📍 {getLocation(job)}</div>
                  <div className="job-salary">{formatSalary(job.salary_min, job.salary_max)}</div>
                  {job.description && (
                    <p className="job-description">
                      {job.description.substring(0, 150)}...
                    </p>
                  )}
                  <div className="job-actions">
                    <button 
                      onClick={() => handleViewDetails(job)}
                      className="btn-apply"
                    >
                      View Details
                    </button>
                    <button
                      className={`btn-save ${savedJobIds.has(String(job.id)) ? 'saved' : ''}`}
                      onClick={() => handleSaveJob(job)}
                    >
                      {savedJobIds.has(String(job.id)) ? '♥' : '♡'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            <div className="pagination-controls">
              <button 
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                ← Previous
              </button>
              
              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>
              
              <button 
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Next →
              </button>
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Jobs;
